"use client";

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { withSearchParams } from "@trydig/ui/next";
import { createContext, type PropsWithChildren, use, useRef } from "react";
import type { ISbStoryData } from "storyblok-js-client";
import { rpc } from "#rpc";
import type { ArticlesRoute } from "@/data/routes/articles.route";
import {
	ARTICLES_FILTER_QUERY,
	ARTICLES_PAGE_SIZE,
} from "@/features/articles/config";
import type { ArticleFilterTag } from "@/features/articles/types";
import { useTranslations } from "@/i18n/client";
import type { BlokArticlePage } from "@/storyblok";

interface ArticlesProviderProps {
	featuredArticles?: ISbStoryData<BlokArticlePage>[];
	initialArticles: ArticlesRoute["byStorySlug"];
	articleFilterTags: ArticleFilterTag[];
	slug: string;
}

const ArticlesContext = createContext<
	Omit<ArticlesProviderProps, "slug" | "initialArticles"> & {
		query: ReturnType<typeof useArticleListingQuery>;
	}
>(null!);

const filterArticlesByActiveTag = (
	articles?: ISbStoryData<BlokArticlePage>[],
	activeilterTag?: ArticleFilterTag,
) => {
	if (!activeilterTag) {
		return articles;
	}

	return articles?.filter((article) =>
		article.tag_list.includes(activeilterTag?.name ?? ""),
	);
};

export const ArticlesProvider = withSearchParams<
	PropsWithChildren<ArticlesProviderProps>
>(
	({
		children,
		featuredArticles,
		initialArticles,
		articleFilterTags,
		searchParams,
		slug,
	}) => {
		const activeTag = articleFilterTags.find(
			(tag) => tag.name === searchParams.get(ARTICLES_FILTER_QUERY),
		);

		const t = useTranslations();

		const query = useArticleListingQuery({
			initialData: {
				articles: initialArticles?.articles,
				total: initialArticles.total,
				error: initialArticles.error,
			},
			featuredArticlesContentIds: featuredArticles?.map(
				(article) => article.content._uid,
			),
			tags: activeTag
				? [activeTag?.name]
				: articleFilterTags.map((tag) => tag.name),
			slug,
		});

		const extendedTags = [
			{
				name: t("All"),
				root: true,
			},
			...articleFilterTags,
		];

		return (
			<ArticlesContext.Provider
				value={{
					featuredArticles: filterArticlesByActiveTag(
						featuredArticles,
						activeTag,
					),
					query,
					articleFilterTags: extendedTags,
				}}
			>
				{children}
			</ArticlesContext.Provider>
		);
	},
);

export const useArticles = () => {
	const ctx = use(ArticlesContext);

	if (!ctx) {
		throw new Error("useArticles must be used within a ArticlesProvider");
	}
	return ctx;
};

const useArticleListingQuery = ({
	initialData,
	slug,
	tags,
	featuredArticlesContentIds,
}: {
	initialData: ArticlesRoute["byStorySlug"];
	slug: string;
	tags?: string[];
	featuredArticlesContentIds?: string[];
}) => {
	const trackedInitialData = useRef<ArticlesRoute["byStorySlug"] | undefined>(
		initialData,
	);

	return useInfiniteQuery({
		queryKey: ["articleListing", slug, tags],
		queryFn: async ({ pageParam }) => {
			const data = await rpc.articles.byStorySlug(slug, tags, pageParam, {
				_uid: {
					not_in: featuredArticlesContentIds?.join(","),
				},
			});

			trackedInitialData.current = data;

			return data;
		},
		initialData: trackedInitialData.current
			? {
					pageParams: [1],
					pages: [trackedInitialData.current],
				}
			: undefined,
		initialPageParam: 1,
		getNextPageParam: (lastPage, allPages) => {
			return lastPage.total! > ARTICLES_PAGE_SIZE * allPages.length
				? allPages.length + 1
				: undefined;
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});
};
